<template>
  <div>
    <div class="d-flex flex-wrap" style="gap: 16px;">
      <v-text-field class="flex-grow-1" :label="$t('betriebsanweisung.tag')" v-model="value.tag" hide-details="auto" data-cy="tag" />
      <v-select class="flex-grow-1" :items="colorOptions" :label="$t('betriebsanweisung.color')" v-model="value.color" clearable
        hide-details="auto" item-text="label" item-value="value">
        <template #selection="{ item }">
          <div class="d-flex align-center">
            <span :style="dotStyle(item.value)"></span>{{ item.label }}
          </div>
        </template>
        <template #item="{ item }">
          <div class="d-flex align-center">
            <span :style="dotStyle(item.value)"></span>{{ item.label }}
          </div>
        </template>
      </v-select>
    </div>

    <div class="subtitle-1 mt-4">{{ $t("operatingInstructions.addFile") }}</div>
    <input ref="fileInput" class="mt-2" type="file" :multiple="config?.multiple" @change="handleFiles"
      accept="application/msword, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/html, image/jpeg, application/vnd.oasis.opendocument.presentation, application/vnd.oasis.opendocument.spreadsheet, application/vnd.oasis.opendocument.text, image/png, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />

    <div class="mt-3" aria-live="polite">
      <div class="caption" v-if="value.files?.length">
        {{ $t("operatingInstructions.filesAdded", { count: value.files.length }) }}
      </div>
      <ul v-if="value.files?.length" class="pl-4">
        <li v-for="(f, i) in value.files" :key="f.name + i">
          {{ f.name }}
          <v-icon small @click="removeFile(i)" data-cy="delete">delete</v-icon>
        </li>
      </ul>
    </div>

    <div class="mt-4" v-if="labels.length">
      <div class="subtitle-2 mb-1">
        {{ $t("operatingInstructions.existingLabels") }}
      </div>
      <div class="label-list">
        <div v-for="label in labels" :key="label._id" class="label-pill"
          :style="{ backgroundColor: label.color || 'transparent' }" @click="applyLabel(label)">
          <span class="label-pill-text">{{ label.tag }}</span>
          <!-- 👇 NEW: delete icon -->
          <v-icon small class="ml-2" @click.stop="deleteLabel(label)" data-cy="delete-label">
            delete
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: Object, required: true },
    config: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      colorOptions: [
        { label: 'Red', value: 'red' },
        { label: 'Green', value: 'green' },
        { label: 'Yellow', value: 'gold' },
        { label: 'Blue', value: 'deepskyblue' },
        { label: 'Orange', value: 'orange' },
        { label: 'Purple', value: 'purple' },
      ],
      labels: [],
    };
  },
  async mounted() {
    await this.fetchLabels();
    this.$root.$on("operatingInstructionLabelsUpdated", this.fetchLabels);
  },
  beforeDestroy() {
    this.$root.$off("operatingInstructionLabelsUpdated", this.fetchLabels);
  },
  methods: {
    async fetchLabels() {
      try {
        const { data } = await this.$store.dispatch("getOperatingInstructionLabels");
        this.labels = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("Failed to load labels", e);
      }
    },
    dotStyle(c) {
      return {
        display: "inline-block",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        marginRight: "8px",
        backgroundColor: c || "transparent",
      };
    },
    async deleteLabel(label) {
      if (!label || !label._id) return;

      try {
        await this.$store.dispatch("deleteOperatingInstructionLabel", {
          id: label._id,
        });

        this.labels = this.labels.filter((l) => l._id !== label._id);

        this.$root.$emit("operatingInstructionLabelsUpdated");
      } catch (e) {
        console.error("Failed to delete label", e);
      }
    },
    applyLabel(label) {
      this.$set(this.value, "tag", label.tag || "");
      this.$set(this.value, "color", label.color || "");
      this.$set(this.value, "labelId", label._id || null);
    },
    async handleFiles(e) {
      const fileList = Array.from(e.target.files || []);
      if (!fileList.length) return;

      if (!Array.isArray(this.value.files)) {
        this.$set(this.value, "files", []);
      }

      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const base64 = String(ev.target.result).split(",")[1];
            const ext = (file.name.match(/\.[0-9a-z]+$/i)?.[0] || "").replace(".", "");
            resolve({
              name: file.name,
              filetype: ext || file.type || "",
              content: base64,
              tag: this.value.tag,
              color: this.value.color,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

      const encoded = await Promise.all(fileList.map(toBase64));
      this.value.files.push(...encoded);
      this.$refs.fileInput.value = "";
    },
    removeFile(i) {
      this.value.files.splice(i, 1);
    },
  },
};
</script>

<style>
.label-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}


.label-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-size: 0.875rem;
  color: #fff;
}

.label-pill-text {
  white-space: nowrap;
}
</style>
